import {
  AlmanacUpdate,
  resolveOriginCategory,
} from "portal/utils/categoryCollection/category";
import { asOneOf, useQuery } from "portal/utils/hooks/useQuery";
import { CategorizedBar } from "portal/components/categoryCollectionProfile/categorized/CategorizedBar";
import { Category } from "protos/robot_syncer/plant_profile";
import { CategoryTemplateAllImages } from "portal/components/categoryTemplates/CategoryTemplateAllImagesTab";
import { CategoryTemplateEditTab } from "portal/views/categoryTemplates/tabs";
import { CategoryTemplateInstructionsDialog } from "portal/components/categoryCollectionProfile/instructions/CategoryTemplateInstructionsDialog";
import { classes } from "portal/theme/theme";
import { RadioChipOption } from "portal/components/RadioChips";
import { ShootTimeSection } from "portal/components/shootTime/ShootTimeSection";
import { Tab, Tabs } from "@mui/material";
import { TAB_QUERY } from "portal/utils/routing";
import { TabContext, TabPanel } from "@mui/lab";
import { ThumbnailControls } from "portal/components/categoryCollectionProfile/ThumbnailControls";
import { useCarbonDefaultAlmanac } from "portal/components/shootTime/useCarbonDefaultAlmanac";
import { useProtectedThumbnailSize } from "portal/components/images/imageSizing/useProtectedThumbnailSize";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import React, { FC, ReactNode } from "react";

interface CategoryTemplateEditProps {
  category: Category;
  categoryOptions: RadioChipOption[];
  categorizationMap: Record<string, string>;
  floatingSaveAction: ReactNode;
  isImageDirty: boolean;
  onImageToggle: (predictionPointId: string) => void;
  isShootTimeDirty: boolean;
  isShootTimeInvalid: boolean;
  onShootTimeChange: (update: AlmanacUpdate) => void;
  onShootTimeReset: () => void;
  onShootTimeValidityChange: (fieldId: string, isValid: boolean) => void;
}

export const CategoryTemplateEdit: FC<CategoryTemplateEditProps> = ({
  category,
  categoryOptions,
  categorizationMap,
  floatingSaveAction,
  isImageDirty,
  onImageToggle,
  isShootTimeDirty,
  isShootTimeInvalid,
  onShootTimeChange,
  onShootTimeReset,
  onShootTimeValidityChange,
}) => {
  const { t } = useTranslation();
  const { almanac: carbonDefaultAlmanac } = useCarbonDefaultAlmanac();
  const [tabParam, setTab] = useQuery(
    TAB_QUERY,
    asOneOf(CategoryTemplateEditTab.ALL, CategoryTemplateEditTab.SHOOT_TIME)
  );
  const tab = tabParam ?? CategoryTemplateEditTab.ALL;
  const { categoryCollection } = useUserPreferences();
  const {
    thumbnailDivRef,
    measuredThumbnailMaxSize,
    thumbnailSize,
    dimensions,
  } = useProtectedThumbnailSize(categoryCollection.thumbnailSize);

  return (
    <>
      <div className="flex flex-col gap-1 pt-2">
        <div className="w-full flex justify-end">
          <ThumbnailControls
            measuredThumbnailMaxSize={measuredThumbnailMaxSize}
            thumbnailSize={thumbnailSize}
          />
        </div>
        <CategorizedBar
          categoryId={category.id}
          categoryOptions={categoryOptions}
          predictionPointIds={category.chipIds}
          dimensions={dimensions}
          categorizationMap={categorizationMap}
          hideCategoryLabel
          onRemove={onImageToggle}
        />
      </div>
      <TabContext value={tab}>
        <div className="flex flex-col md:flex-row justify-center md:justify-between items-start md:items-end">
          <div className="flex items-center gap-6">
            <Tabs
              value={tab}
              onChange={(
                _: React.SyntheticEvent,
                newValue: CategoryTemplateEditTab
              ) => {
                setTab(newValue);
              }}
            >
              <Tab
                label={
                  <TabLabel
                    label={t(
                      "components.categoryCollectionProfile.images.allImages"
                    )}
                    highlight={isImageDirty}
                  />
                }
                className="text-inherit"
                value={CategoryTemplateEditTab.ALL}
              />
              <Tab
                label={
                  <TabLabel
                    label={t("views.categoryTemplates.tabs.shootTime")}
                    highlight={isShootTimeDirty}
                    error={isShootTimeInvalid}
                  />
                }
                className="text-inherit"
                value={CategoryTemplateEditTab.SHOOT_TIME}
              />
            </Tabs>
            <CategoryTemplateInstructionsDialog />
          </div>
        </div>
        <TabPanel
          value={CategoryTemplateEditTab.ALL}
          className={classes("pt-2 pl-0 pr-0", "overflow-visible")}
        >
          <div ref={thumbnailDivRef}>
            <CategoryTemplateAllImages
              categoryOptions={categoryOptions}
              categorizationMap={categorizationMap}
              dimensions={dimensions}
              floatingAction={floatingSaveAction}
              scopeCustomerUuid={category.customerId}
              onImageToggle={onImageToggle}
            />
          </div>
        </TabPanel>
        <TabPanel
          value={CategoryTemplateEditTab.SHOOT_TIME}
          className="pt-2 pl-0 pr-0"
        >
          <ShootTimeSection
            almanac={category.almanac}
            carbonDefaultAlmanac={carbonDefaultAlmanac}
            originCategory={resolveOriginCategory(
              category.almanac?.originCategoryId
            )}
            subtitle={t("views.categoryTemplates.shootTime.subnote")}
            canEdit={true}
            onChange={(formulas) => {
              onShootTimeChange({ formulas });
            }}
            onSizesChange={(sizeBoundariesMm) => {
              onShootTimeChange({ sizeBoundariesMm });
            }}
            onReset={onShootTimeReset}
            isResettable={isShootTimeDirty}
            onFieldValidityChange={onShootTimeValidityChange}
          />
        </TabPanel>
      </TabContext>
    </>
  );
};

interface TabLabelProps {
  label: string;
  highlight: boolean;
  error?: boolean;
}
const TabLabel: FC<TabLabelProps> = ({ label, highlight, error }) => (
  <span className="flex items-center gap-2">
    {label}
    {(highlight || error) && (
      <span
        className={classes("size-2 rounded-full", {
          "bg-warning": error,
          "bg-carbon-orange": highlight && !error,
        })}
        aria-hidden
      />
    )}
  </span>
);
